import React, { ReactElement, useMemo, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  ArchiveRestore,
  ChevronLeft,
  CircleCheck,
  Package,
  PauseCircle,
  Pencil,
  PlayCircle,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  useCloseIncident,
  useDeleteIncident,
  useDeleteIncidentPause,
  usePauseIncident,
  useProduct,
  useProductIncidents,
  useReopenIncident,
  useUpdateIncident,
  useUpdateIncidentPause,
} from '@data';
import { reasonOf } from '@data/utils/errors';
import { useOrganizationId } from '@app/utils/hooks';
import { capitalize, dayKey, toFixedHours } from '@app/utils';
import { DetailRow, Switch } from '@app/components/core';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import { incidentsPath } from '@app/constants/url';
import { cardClass, pageTitleClass, tabClass } from '@app/constants/styles';
import { Loader } from '@ui';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { Textarea } from '@ui/components/ui/textarea';
import DateTimeField from '@app/components/shared/reportModal/DateTimeField';
import { clockTimeOf } from '@app/components/shared/phillReport/incidentLength';
import { TabBar } from '@app/components/core/tab-bar';
import ConfirmDialog from '../ConfirmDialog';
import IncidentCloseDialog from '../clientReporting/product/IncidentCloseDialog';
import IncidentResolveDialog from '../clientReporting/product/IncidentResolveDialog';
import { IncidentAction, incidentActionsFor } from '../clientReporting/product/sources/incidentActions';
import IncidentPauseDialog from '../clientReporting/product/IncidentPauseDialog';
import IncidentTimeline from './IncidentTimeline';
import IncidentWorkLog from '../clientReporting/product/IncidentWorkLog';
import {
  IncidentDraft,
  IncidentDraftField,
  PRIORITIES,
  Priority,
  SlaOutcome,
  agreementForLevel,
  draftErrors,
  draftFromIncident,
  formatTarget,
  incidentFromApi,
  incidentInputFromDraft,
  isClosed,
  isDraftValid,
  localToIso,
  priorityLabel,
  resolutionPatch,
  resolveError,
  toLocalInput,
  viewIncident,
} from '../clientReporting/product/sources/incidents';

// P1 alone is coloured: it means the product is down. A warning on the common priorities warns of nothing.
const PRIORITY_STYLE: Record<Priority, string> = {
  P1: 'bg-red-500/10 text-red-600 dark:text-red-400',
  P2: 'bg-muted-foreground/20 text-muted-foreground',
  P3: 'bg-muted-foreground/20 text-muted-foreground',
  P4: 'bg-muted-foreground/20 text-muted-foreground',
};

/** Refusals this page can produce that are worth a sentence of their own; the rest read as a failed save. */
const REFUSALS = new Set([
  'pauseAfterResolved',
  'pauseRunsPastResolved',
  'pauseRunsPastFix',
  'hoursBookedAgainstIncident',
]);

const OUTCOME_STYLE: Record<SlaOutcome, string> = {
  met: 'text-emerald-600 dark:text-emerald-400',
  missed: 'text-red-600 dark:text-red-400',
  pending: 'text-muted-foreground',
};

const dateTime = (isoInstant: string, language: string): string =>
  new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(isoInstant));

/** The day it came in, for the header. The exact minute is on the Reported row. */
const day = (isoInstant: string, language: string): string =>
  new Intl.DateTimeFormat(language, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(isoInstant));

/** One incident: what it was, how it went against the agreement, and the two answers that move it along. */
const IncidentDetail = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const organizationId = useOrganizationId();
  const { productId, incidentId } = useParams<{ productId: string; incidentId: string }>();

  // Two tabs from this slice on: the Details card, and where this incident's time went.
  const [view, setView] = useState<'details' | 'time'>('details');
  /** The day the header's own Add work asked for, handed to the log because it owns the form. */
  const [workDayToAdd, setWorkDayToAdd] = useState<string>();
  const [expandAll, setExpandAll] = useState(false);
  const [isPausing, setIsPausing] = useState(false);

  const [draft, setDraft] = useState<IncidentDraft | null>(null);
  /** The fix time being corrected on its own row, apart from the editor below. Null while nobody is. */
  const [fixDraft, setFixDraft] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  /** The reason code of the last refusal, so a rule the server names is said rather than "it failed". */
  const [saveFailed, setSaveFailed] = useState<string | null>(null);

  const { label: backLabel, goBack } = useBackNavigation(
    incidentsPath(organizationId ?? ''),
    t('incidents.backToIncidents'),
  );

  const { data: product, isLoading } = useProduct(productId);
  const { data: incidentData, isLoading: isLoadingIncidents } = useProductIncidents(productId);
  const updateIncident = useUpdateIncident();
  const deleteIncident = useDeleteIncident();
  const closeIncident = useCloseIncident();
  const reopenIncident = useReopenIncident();
  const pauseIncident = usePauseIncident();
  const updateIncidentPause = useUpdateIncidentPause();
  const deleteIncidentPause = useDeleteIncidentPause();

  const entity = useMemo(
    () => (incidentData?.items ?? []).find(candidate => candidate.id === incidentId) ?? null,
    [incidentData?.items, incidentId],
  );

  const agreement = useMemo(() => agreementForLevel(entity?.supportLevel), [entity?.supportLevel]);

  // Apart from the judged view: an incident on a product taken off support has nothing left to judge it by.
  const stored = useMemo(() => (entity ? incidentFromApi(entity) : null), [entity]);
  const incident = useMemo(() => (stored ? viewIncident(stored, agreement) : null), [stored, agreement]);

  const productName = capitalize(product?.name) ?? '';

  const back = (
    <button
      type="button"
      onClick={goBack}
      className="mb-3.5 flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground print:hidden"
    >
      <ChevronLeft className="size-4" />
      {backLabel}
    </button>
  );

  if (isLoading || isLoadingIncidents) return <Loader />;

  if (!incident || !productId) {
    return (
      <div className="w-full">
        {back}
        <p className="rounded-xl border border-dashed border-border px-3 py-10 text-center text-sm text-muted-foreground">
          {t('incidents.notFound')}
        </p>
      </div>
    );
  }

  const editing = draft !== null;

  const startEditing = (): void => setDraft(draftFromIncident(incident));

  const stopEditing = (): void => setDraft(null);

  // A refused write leaves the old version on screen, so say so rather than closing the editor.
  const done = { onSuccess: () => stopEditing(), onError: error => setSaveFailed(reasonOf(error) || 'saveFailed') };

  const save = (): void => {
    if (!draft) return;
    updateIncident.mutate({ productId, incidentId: incident.id, values: incidentInputFromDraft(draft) }, done);
  };

  const canSave = !!draft && isDraftValid(draft);

  // Removing lives here alone: it is the whole record going, and this page is what the record is.
  const confirmRemove = (): void =>
    deleteIncident.mutate(
      { productId, incidentId: incident.id },
      { onSuccess: () => goBack(), onError: error => setSaveFailed(reasonOf(error) || 'saveFailed') },
    );

  // The correction endpoint, not the closing one: anybody on the product can say when it was fixed.
  /** The refusal in the page's own words, where it has them: a reason code the server named. */
  const refusal = saveFailed
    ? t(REFUSALS.has(saveFailed) ? `incidents.error.${saveFailed}` : 'incidents.saveFailed')
    : null;

  const confirmResolve = (atLocal: string, alsoClose: boolean): void => {
    const target = { productId, incidentId: incident.id };
    const resolved = {
      onSuccess: () => setIsResolving(false),
      onError: error => setSaveFailed(reasonOf(error) || 'saveFailed'),
    };

    // Closing takes the moment with it, so both answers land in one write.
    if (alsoClose) closeIncident.mutate({ ...target, values: resolutionPatch(atLocal) }, resolved);
    else updateIncident.mutate({ ...target, values: resolutionPatch(atLocal) }, resolved);
  };

  // On the row that shows it, because the fix time is the Resolve button's to set rather than the editor's.
  const saveFixTime = (): void => {
    if (fixDraft === null || !stored || resolveError(stored, fixDraft)) return;
    updateIncident.mutate(
      { productId, incidentId: incident.id, values: resolutionPatch(fixDraft) },
      { onSuccess: () => setFixDraft(null), onError: error => setSaveFailed(reasonOf(error) || 'saveFailed') },
    );
  };

  // The way back from a Resolve nobody meant to press.
  const clearFixTime = (): void =>
    updateIncident.mutate(
      { productId, incidentId: incident.id, values: { resolvedAt: null } },
      { onError: error => setSaveFailed(reasonOf(error) || 'saveFailed') },
    );

  const confirmClose = (): void =>
    closeIncident.mutate(
      { productId, incidentId: incident.id, values: {} },
      { onSuccess: () => setIsClosing(false), onError: error => setSaveFailed(reasonOf(error) || 'saveFailed') },
    );

  // One open pause at a time, so the button is a pair; ending it writes to the pause, not the incident.
  const running = (incident.pauses ?? []).find(pause => !pause.endedAt) ?? null;

  const confirmPause = (atLocal: string, endLocal: string, reason: string): void => {
    const done = {
      onSuccess: () => setIsPausing(false),
      onError: error => setSaveFailed(reasonOf(error) || 'saveFailed'),
    };

    if (running) {
      updateIncidentPause.mutate(
        { productId, incidentId: incident.id, pauseId: running.id, values: { endedAt: localToIso(atLocal) } },
        done,
      );
      return;
    }

    pauseIncident.mutate(
      {
        productId,
        incidentId: incident.id,
        values: {
          startedAt: localToIso(atLocal),
          // Blank is an answer: the wait is still on, and the band runs to now until somebody ends it.
          ...(endLocal ? { endedAt: localToIso(endLocal) } : {}),
          ...(reason ? { reason } : {}),
        },
      },
      done,
    );
  };

  // No dialog: reopening decides nothing and takes nothing away.
  const confirmReopen = (): void =>
    reopenIncident.mutate(
      { productId, incidentId: incident.id },
      { onError: error => setSaveFailed(reasonOf(error) || 'saveFailed') },
    );

  // No confirmation on the bin: a pause removed by mistake is re-added in the dialog it came from.
  const pausedRows = (incident.pauses ?? []).map(pause => {
    const from = new Date(pause.startedAt);
    const to = pause.endedAt ? new Date(pause.endedAt) : null;
    // The day once, then the clock: two full timestamps of the same afternoon is the date read twice.
    const sameDay = !!to && to.toDateString() === from.toDateString();
    let until = t('incidents.pauseStillRunning');
    if (to) until = sameDay ? clockTimeOf(to) : dateTime(pause.endedAt as string, language);

    return (
      <div key={pause.id} className="flex items-baseline gap-2 py-2 first:pt-0 last:pb-0">
        <span className="tabular-nums">{`${dateTime(pause.startedAt, language)} - ${until}`}</span>
        {pause.reason && <span className="min-w-0 truncate text-muted-foreground">{pause.reason}</span>}
        {/* Gone once the record is closed, which is when the server stops accepting the write too. */}
        {!isClosed(incident) && (
          <Button
            variant="ghost"
            size="icon"
            className="-my-1 ml-auto size-7 shrink-0 text-muted-foreground hover:text-destructive print:hidden"
            aria-label={t('incidents.removePause')}
            onClick={() =>
              deleteIncidentPause.mutate(
                { productId, incidentId: incident.id, pauseId: pause.id },
                { onError: error => setSaveFailed(reasonOf(error) || 'saveFailed') },
              )
            }
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
    );
  });

  // What each action looks like at the page's own size. Which is offered is the shared rule's call.
  const headerButtons: Record<IncidentAction, ReactElement> = {
    [IncidentAction.resolve]: (
      <Button
        key="resolve"
        className="shrink-0"
        onClick={() => {
          setSaveFailed(null);
          setIsResolving(true);
        }}
      >
        <CircleCheck className="size-4" />
        {t('incidents.resolve')}
      </Button>
    ),
    [IncidentAction.close]: (
      <Button key="close" className="shrink-0" onClick={() => setIsClosing(true)}>
        <Package className="size-4" />
        {t('incidents.close')}
      </Button>
    ),
    [IncidentAction.reopen]: (
      <Button key="reopen" variant="outline" className="shrink-0 bg-card" onClick={confirmReopen}>
        <ArchiveRestore className="size-4" />
        {t('incidents.reopen')}
      </Button>
    ),
  };

  // Nothing at all while it is unresolved: a pill on the state every incident starts in is a label, not news.
  let status = '';
  if (isClosed(incident)) status = t('incidents.statusClosed');
  else if (incident.resolvedAt) status = t('incidents.statusResolved');

  const answered =
    incident.respondedAt === null ? (
      <span className={OUTCOME_STYLE.pending}>{t('incidents.notAnswered')}</span>
    ) : (
      <span>{dateTime(incident.respondedAt, language)}</span>
    );

  const fixTimeError = fixDraft !== null && stored ? resolveError(stored, fixDraft) : null;

  // Shown only once there is one: a row reading "not resolved yet" states the state the page is already in.
  const fixTime =
    fixDraft === null ? (
      <div className="flex items-center gap-1">
        <span>{dateTime(incident.resolvedAt as string, language)}</span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground hover:text-foreground print:hidden"
          aria-label={t('incidents.editFixTime')}
          onClick={() => setFixDraft(toLocalInput(incident.resolvedAt))}
        >
          <Pencil className="size-3.5" />
        </Button>
        {!isClosed(incident) && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-destructive print:hidden"
            aria-label={t('incidents.clearFixTime')}
            onClick={clearFixTime}
          >
            <Trash2 className="size-3.5" />
          </Button>
        )}
      </div>
    ) : (
      <div className="flex max-w-sm flex-col gap-1">
        <DateTimeField
          id="incident-resolvedAt"
          label={t('reportIncident.day')}
          value={fixDraft}
          onChange={setFixDraft}
          invalid={!!fixTimeError}
        />
        {fixTimeError && <p className="mt-1 text-[11px] text-destructive">{t(`incidents.error.${fixTimeError}`)}</p>}
        <div className="flex gap-2 print:hidden">
          <Button variant="ghost" size="sm" onClick={() => setFixDraft(null)}>
            {t('actions.cancel')}
          </Button>
          <Button size="sm" disabled={!!fixTimeError} onClick={saveFixTime}>
            {t('actions.save')}
          </Button>
        </div>
      </div>
    );

  const editButtons = editing ? (
    <div className="flex shrink-0 gap-2 print:hidden">
      <Button variant="ghost" size="sm" onClick={stopEditing}>
        {t('actions.cancel')}
      </Button>
      <Button size="sm" disabled={!canSave} onClick={save}>
        {t('actions.save')}
      </Button>
    </div>
  ) : (
    <div className="flex shrink-0 gap-2 print:hidden">
      <Button variant="outline" size="sm" onClick={startEditing}>
        <Pencil className="size-4" />
        {t('edit')}
      </Button>
    </div>
  );

  // Once there are hours against it, removing the incident would take their reason with it, and a closed
  // record carries a manager's sign-off that deleting would erase. The server refuses both.
  const canRemove = incident.hours === 0 && !incident.closedAt;

  const errors = draft ? draftErrors(draft) : {};
  const set = (field: keyof IncidentDraft, value: string): void =>
    setDraft(current => (current ? { ...current, [field]: value } : current));
  const fieldError = (field: IncidentDraftField): ReactElement | null =>
    errors[field] ? <p className="mt-1 text-[11px] text-destructive">{t(`incidents.error.${errors[field]}`)}</p> : null;

  // The label is the row's own <dt>, so the field carries only the control and its error.
  const timestamp = (field: 'reportedAt' | 'respondedAt', hint?: string): ReactElement => (
    <div className="flex max-w-sm flex-col gap-1">
      <DateTimeField
        id={`incident-${field}`}
        // The row's own name is above it, so these two say which half they are rather than repeating it.
        label={t('reportIncident.day')}
        value={draft?.[field] ?? ''}
        onChange={next => set(field, next)}
        invalid={!!errors[field]}
      />
      {fieldError(field) ?? (hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null)}
    </div>
  );

  const titleField = draft && (
    <div className="flex max-w-md flex-col gap-1">
      <Input
        id="incident-title"
        aria-label={t('incidents.issueColumn')}
        value={draft.title}
        aria-invalid={!!errors.title}
        placeholder={t('incidents.titlePlaceholder')}
        className={cx('bg-popover', errors.title && 'border-destructive')}
        onChange={event => set('title', event.target.value)}
      />
      {fieldError('title')}
    </div>
  );

  const descriptionField = draft && (
    <Textarea
      id="incident-description"
      aria-label={t('incidents.description')}
      rows={4}
      value={draft.description}
      placeholder={t('incidents.descriptionPlaceholder')}
      className="bg-popover"
      onChange={event => set('description', event.target.value)}
    />
  );

  // Line breaks kept, because a write-up is usually an account with a list under it.
  const writtenUp = incident.description ? (
    <p className="whitespace-pre-line">{incident.description}</p>
  ) : (
    <span className="text-muted-foreground">{t('incidents.noDescription')}</span>
  );

  // Read off the draft while editing, so choosing P1 shows what P1 costs before Save rather than after.
  const targetFor = (priority: Priority): { responseHours: number; resolutionHours: number } | null =>
    agreement?.targets.find(entry => entry.priority === priority) ?? incident.target;

  const target = targetFor(draft?.priority || incident.priority);

  const priorityField = draft && (
    <div className="max-w-sm">
      <Select
        value={draft.priority}
        onValueChange={next => next && set('priority', next as Priority)}
        items={selectItemsByValue(
          PRIORITIES.map(priority => ({
            value: priority,
            label: priorityLabel(priority, t(`incidents.priorityName.${priority}`)),
          })),
        )}
      >
        <SelectTrigger aria-label={t('incidents.priority')} className="bg-popover">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRIORITIES.map(priority => (
            <SelectItem key={priority} value={priority}>
              {priorityLabel(priority, t(`incidents.priorityName.${priority}`))}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  // The agreement is the product's, not this incident's, so it is a card of its own and read-only here.
  // Absent once the product is off support: there is no agreement left to print.
  const productCard = agreement && (
    <section className={cardClass}>
      <div className="mb-5 flex items-center justify-between gap-4">
        <h2 className="text-lg font-bold text-foreground">{t('incidents.productInformation')}</h2>
      </div>

      <dl className="divide-y divide-border">
        <DetailRow label={t('incidents.supportAgreement')}>{t(`incidents.level.${agreement.level}`)}</DetailRow>
        <DetailRow label={t('incidents.serviceWindow')}>{agreement.window}</DetailRow>
        <DetailRow label={t('incidents.aroundTheClock')}>
          {agreement.aroundTheClock.length
            ? t('incidents.aroundTheClockPriorities', { priorities: agreement.aroundTheClock.join(', ') })
            : t('incidents.noneAroundClock')}
        </DetailRow>
      </dl>
    </section>
  );

  // Target sits under Priority because that is what the priority buys.
  const incidentCard = (
    <section className={cx(cardClass, 'mb-6')}>
      <div className="mb-5 flex items-center justify-between gap-4">
        <h2 className="text-lg font-bold text-foreground">{t('incidents.incidentDetails')}</h2>
        {editButtons}
      </div>

      <dl className="divide-y divide-border">
        <DetailRow label={t('incidents.issueColumn')}>{draft ? titleField : incident.title}</DetailRow>

        <DetailRow label={t('incidents.description')}>{draft ? descriptionField : writtenUp}</DetailRow>

        <DetailRow label={t('incidents.priority')}>
          {draft ? priorityField : priorityLabel(incident.priority, t(`incidents.priorityName.${incident.priority}`))}
        </DetailRow>

        <DetailRow label={t('incidents.target')}>
          {target ? (
            t('incidents.targetHint', {
              response: formatTarget(target.responseHours),
              resolution: formatTarget(target.resolutionHours),
            })
          ) : (
            <span className="text-muted-foreground">{t('incidents.noAgreement')}</span>
          )}
        </DetailRow>

        <DetailRow label={t('incidents.reportedAt')}>
          {draft ? timestamp('reportedAt') : dateTime(incident.reportedAt, language)}
        </DetailRow>

        <DetailRow label={t('incidents.respondedAt')}>
          {draft ? timestamp('respondedAt', t('incidents.blankMeansOpen')) : answered}
        </DetailRow>

        {incident.resolvedAt !== null && <DetailRow label={t('incidents.resolvedAt')}>{fixTime}</DetailRow>}

        {/* The tables' own name for this figure, so one number is not two things across two screens. */}
        <DetailRow label={t('incidents.hoursReported')}>
          <span className="font-semibold tabular-nums">{`${toFixedHours(incident.hours)}h`}</span>
        </DetailRow>
      </dl>
    </section>
  );

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      {back}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-6">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {/* The product and the day it came in: enough to know which incident this is. */}
          <h1 className={cx('m-0', pageTitleClass)}>
            {productName}{' '}
            <span className="text-xl font-normal text-muted-foreground md:text-2xl">
              {day(incident.reportedAt, language)}
            </span>
          </h1>
          {/* A pill, not a tag: it names the incident's class rather than marking a state. */}
          <span className={cx('rounded-full px-2.5 py-1 text-sm font-bold', PRIORITY_STYLE[incident.priority])}>
            {incident.priority}
          </span>
          {status && (
            <span className="rounded border border-muted-foreground/50 bg-card px-2 py-[3px] text-xs font-normal leading-none text-muted-foreground">
              {status}
            </span>
          )}
        </div>

        {/* Beside the status they change. The one the record is waiting for is filled, Edit below is not. */}
        {!editing && (
          <div className="flex shrink-0 items-center gap-2 print:hidden">
            {/* Until it is closed. A wait is often written down after the fix, and closing is what puts
                the figures beyond changing. */}
            {!isClosed(incident) && (
              <Button
                variant="outline"
                className="shrink-0 bg-card"
                onClick={() => {
                  // The log lives on the other tab, so the press takes you there as well as opening the form.
                  setView('time');
                  setWorkDayToAdd(dayKey(new Date()));
                }}
              >
                <Plus className="size-4" />
                {t('incidents.addWork')}
              </Button>
            )}
            {!isClosed(incident) && (
              <Button variant="outline" className="shrink-0 bg-card" onClick={() => setIsPausing(true)}>
                {running ? <PlayCircle className="size-4" /> : <PauseCircle className="size-4" />}
                {t(running ? 'incidents.resume' : 'incidents.pause')}
              </Button>
            )}
            {canRemove && (
              <Button
                variant="outline"
                className="shrink-0 bg-card text-destructive"
                onClick={() => setIsRemoving(true)}
              >
                <Trash2 className="size-4" />
                {t('actions.remove')}
              </Button>
            )}
            {/* The sprint report's own box and crate: closing should read as one verb across the app. */}
            {incidentActionsFor(incident).map(action => headerButtons[action])}
          </div>
        )}
      </div>

      <IncidentPauseDialog
        incident={isPausing ? incident : null}
        ending={!!running}
        onConfirm={confirmPause}
        onCancel={() => setIsPausing(false)}
      />

      <IncidentResolveDialog
        incident={isResolving ? incident : null}
        refusal={refusal}
        onResolve={confirmResolve}
        onCancel={() => setIsResolving(false)}
      />

      <IncidentCloseDialog
        incident={isClosing ? incident : null}
        onClose={confirmClose}
        onCancel={() => setIsClosing(false)}
      />

      <ConfirmDialog
        open={isRemoving}
        onOpenChange={setIsRemoving}
        title={t('incidents.removeTitle')}
        description={t('incidents.removeMeaning')}
        onConfirm={confirmRemove}
      />

      {/* Not "Description": that is a field on the first tab now, and a tab cannot share its name with
          one of the things inside it. */}
      <TabBar wrapperClassName="mb-6 print:hidden" activeKey={view}>
        {(
          [
            ['details', 'incidents.detailsTab'],
            ['time', 'incidents.reportingTab'],
          ] as const
        ).map(([value, labelKey]) => (
          <button
            key={value}
            type="button"
            aria-pressed={view === value}
            onClick={() => setView(value)}
            className={tabClass(view === value)}
          >
            {t(labelKey)}
          </button>
        ))}
      </TabBar>

      {view === 'details' && (
        <>
          {/* Every priority, because the axis is built from the promise rather than fixed: a P1 draws
              thirteen hours and a P4 five days, each ticked at a step that suits its own width. First
              on the page, because it is the whole incident in one look. */}
          {/* Absent once the product is off support: the whole drawing is promises, and there are none. */}
          {incident.target && (
            <div className="mb-6">
              <IncidentTimeline incident={{ ...incident, target: incident.target }} />
            </div>
          )}
          {/* The incident first, then the agreement it is measured against. */}
          {incidentCard}
          {productCard}
        </>
      )}

      {view === 'time' && (
        <div className={cx('flex min-h-[420px] flex-1 flex-col', cardClass)}>
          {/* Corrected on the screen it is read on. These are real time entries, so an hour fixed here is
              fixed in the person's day as well; a client gets the log and none of the buttons. */}
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="text-base font-semibold text-foreground">{t('incidents.whoReported')}</p>
            <Switch value={expandAll} onChange={() => setExpandAll(value => !value)} text={t('titles.expandAll')} />
          </div>

          {/* Always editable: no client can reach this section, so there is no reader to hide the buttons from. */}
          <IncidentWorkLog
            productId={productId}
            incidentId={incident.id}
            expandAll={expandAll}
            canEdit
            // Closing the record is what stops more time going on it, and the server refuses it too.
            canAdd={!incident.closedAt}
            addForDay={workDayToAdd}
            onAddForDayHandled={() => setWorkDayToAdd(undefined)}
          />

          {/* Under the work, on the tab already about where this incident's time went: hours booked and
              hours nobody could book are two halves of one question. Only for an incident that has
              waited on somebody, a heading over nothing being a question nobody asked. */}
          {pausedRows.length > 0 && (
            <div className="mt-6 border-t border-border pt-4">
              <p className="mb-2 text-base font-semibold text-foreground">{t('incidents.pausedSection')}</p>
              <p className="mb-3 text-xs text-muted-foreground">{t('incidents.pausedMeaning')}</p>
              <div className="flex flex-col divide-y divide-border text-sm">{pausedRows}</div>
            </div>
          )}
        </div>
      )}

      {refusal && <p className="mt-3 text-[11px] text-destructive">{refusal}</p>}
    </div>
  );
};

export default IncidentDetail;
